import { useState, type FormEvent } from "react";
import { api, ApiError, type TeamMember } from "../api";
import { useAsync } from "../useAsync";
import { getSession } from "../session";
import { StatusChip } from "../components/StatusChip";
import { shortTime, DASH } from "../format";

/**
 * Team: the people in this account. Owners invite, disable and change roles;
 * everyone shares the same products and queue, and every decision is recorded
 * under the individual's email — that's the point of having users at all.
 * Invites work before the email service exists: the link comes back here for
 * the owner to share.
 */
export function Team() {
  const team = useAsync(() => api.team.list(), []);
  const me = getSession();
  const canManage = me?.account.member_role !== "member" || me?.account.role === "operator";

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<"member" | "owner">("member");
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [invitedEmail, setInvitedEmail] = useState<string | null>(null);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  const invite = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setInviteUrl(null);
    setAlreadyRegistered(false);
    setCopied(false);
    try {
      const res = await api.team.invite(email.trim().toLowerCase(), role);
      setInviteUrl(res.invite_url);
      setInvitedEmail(email.trim().toLowerCase());
      setAlreadyRegistered(res.already_registered);
      setEmail("");
      team.reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const act = async (id: string, fn: () => Promise<unknown>) => {
    setBusy(true);
    setRowError((r) => {
      const { [id]: _drop, ...rest } = r;
      return rest;
    });
    try {
      await fn();
      team.reload();
    } catch (err) {
      setRowError((r) => ({ ...r, [id]: err instanceof ApiError ? err.message : String(err) }));
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!inviteUrl) return;
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setCopied(true);
    } catch {
      /* clipboard blocked — the URL is visible to select manually */
    }
  };

  if (team.error) return <div className="form-error" style={{ marginTop: 20 }}>{team.error}</div>;
  if (team.loading || !team.data) return <div style={{ color: "var(--text-faint)", fontSize: 13, marginTop: 20 }}>Loading team…</div>;

  return (
    <div style={{ maxWidth: 820 }}>
      {canManage && (
        <section className="panel" style={{ marginTop: 18 }}>
          <h2>Invite someone</h2>
          <form onSubmit={invite} style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap", marginTop: 14 }}>
            <div className="field" style={{ flex: 1, minWidth: 220 }}>
              <label htmlFor="t-email">Email</label>
              <input id="t-email" className="input" type="email" required value={email}
                     onChange={(e) => setEmail(e.target.value)} placeholder="teammate@company.com" />
            </div>
            <div className="field" style={{ width: 130 }}>
              <label htmlFor="t-role">Role</label>
              <select id="t-role" className="input" value={role}
                      onChange={(e) => setRole(e.target.value as "member" | "owner")}>
                <option value="member">Member</option>
                <option value="owner">Owner</option>
              </select>
            </div>
            <button type="submit" className="btn-accent" style={{ minHeight: 42 }} disabled={busy}>
              Invite
            </button>
          </form>
          {error && <div className="form-error" style={{ marginTop: 10 }}>{error}</div>}
          {inviteUrl && (
            <div className="banner" style={{ marginTop: 14, flexDirection: "column", alignItems: "stretch", gap: 8 }}>
              <span>
                Invite created for <strong>{invitedEmail}</strong>
                {alreadyRegistered
                  ? " — they already have a FunnelKeeper login, so this just adds this workspace. Share the link:"
                  : " — share this link with them (valid 7 days, works once):"}
              </span>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <code style={{ flex: 1, minWidth: 0, fontSize: 11.5, overflow: "hidden", textOverflow: "ellipsis",
                               whiteSpace: "nowrap", background: "var(--surface)", border: "1px solid var(--border)",
                               borderRadius: 6, padding: "8px 10px" }}>
                  {inviteUrl}
                </code>
                <button type="button" className="btn-ghost" onClick={copy}>{copied ? "Copied" : "Copy"}</button>
              </div>
            </div>
          )}
          <div className="form-note" style={{ marginTop: 10 }}>
            Members do everything except manage the team; owners can also invite, disable and change roles.
          </div>
        </section>
      )}

      <section style={{ marginTop: 30 }}>
        <h2 style={{ marginBottom: 12 }}>Members</h2>
        <div style={{ overflowX: "auto" }}>
          <table className="ptable">
            <thead>
              <tr>
                <th>Person</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last seen</th>
                {canManage && <th></th>}
              </tr>
            </thead>
            <tbody>
              {team.data.map((m) => (
                <MemberRow key={m.id} m={m} isMe={m.email === me?.account.email}
                           canManage={canManage} busy={busy} error={rowError[m.id]} onAct={act} />
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

function MemberRow({
  m, isMe, canManage, busy, error, onAct,
}: {
  m: TeamMember; isMe: boolean; canManage: boolean; busy: boolean; error?: string;
  onAct: (id: string, fn: () => Promise<unknown>) => void;
}) {
  return (
    <tr style={{ cursor: "default" }}>
      <td>
        <div className="cell" style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
          <span>
            {m.name || m.email}
            {isMe && <span className="eyebrow" style={{ marginLeft: 8 }}>you</span>}
          </span>
          {m.name && <span style={{ fontSize: 11.5, color: "var(--text-faint)" }}>{m.email}</span>}
          {error && <span className="form-error" style={{ fontSize: 11.5 }}>{error}</span>}
        </div>
      </td>
      <td><div className="cell" style={{ textTransform: "capitalize" }}>{m.member_role}</div></td>
      <td>
        <div className="cell" style={{ textAlign: "right" }}>
          <StatusChip
            kind={m.status === "active" ? "passing" : m.status === "invited" ? "building" : "pre-gate"}
            label={m.status === "active" ? "Active" : m.status === "invited" ? "Invited" : "Disabled"}
          />
        </div>
      </td>
      <td><div className="cell num">{m.last_seen_at ? shortTime(m.last_seen_at) : DASH}</div></td>
      {canManage && (
        <td>
          <div className="cell" style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
            {!isMe && m.status !== "disabled" && (
              <button type="button" className="btn-link" disabled={busy}
                      onClick={() => onAct(m.id, () => api.team.setRole(m.id, m.member_role === "owner" ? "member" : "owner"))}>
                Make {m.member_role === "owner" ? "member" : "owner"}
              </button>
            )}
            {!isMe && (m.status === "disabled" ? (
              <button type="button" className="btn-link" disabled={busy}
                      onClick={() => onAct(m.id, () => api.team.enable(m.id))}>
                Re-enable
              </button>
            ) : (
              <button type="button" className="btn-link" style={{ color: "var(--neg)" }} disabled={busy}
                      onClick={() => onAct(m.id, () => api.team.disable(m.id))}>
                Disable
              </button>
            ))}
          </div>
        </td>
      )}
    </tr>
  );
}
